import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import {
  allowedStageBackMoves,
  allowedStageMoves,
  MANAGER_ROLES,
  PIPELINE_STAGE_ORDER,
  stageHasLiveDeal,
  stageUndoKind,
  type PipelineCard,
  type PipelineStage,
  type SavedViewDto,
  type StageMoveOptions,
} from '@performance-crm/shared';
import { GripVertical, MoreHorizontal, Plus, Undo2, XCircle } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { QuoteDialog } from '@/components/commercial/QuoteDialog';
import { LeadFollowupBadge, ScoreBadge } from '@/components/leads/badges';
import { LeadFilters } from '@/components/leads/LeadFilters';
import { applyLeadFilter } from '@/components/leads/leadFilterRules';
import { TagChips } from '@/components/leads/TagChips';
import { useCompanyScope } from '@/lib/useCompanyScope';
import { SavedViews } from '@/components/leads/SavedViews';
import { ViewSwitch } from '@/components/leads/ViewSwitch';
import { StageMoveDialog } from '@/components/pipeline/StageMoveDialog';
import { StageUndoDialog } from '@/components/pipeline/StageUndoDialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/components/ui/toast-context';
import { useAuth } from '@/lib/auth-context';
import { fmt, STAGE_LABEL } from '@/lib/format';
import { useCatalogs, usePipeline } from '@/lib/queries';

const FILTER_KEYS = [
  'companyId',
  'projectId',
  'advisorId',
  'followupState',
  'overdueDays',
  'actions',
  'contactState',
  'mediumCategory',
  'q',
  'capturedFrom',
  'capturedTo',
  'tagIds',
  'overdueBand',
  'unassigned',
  'status',
];

export function PipelinePage() {
  const [params, setParams] = useSearchParams();
  const { active: activeCompany } = useCompanyScope();
  const toast = useToast();
  const navigate = useNavigate();
  const queryParams = useMemo(() => {
    const p = new URLSearchParams();
    for (const k of FILTER_KEYS) {
      const v = params.get(k);
      if (v) p.set(k, v);
    }
    // Filtros por campo personalizado (cf_<clave>) tal como vienen de la barra o de una vista guardada.
    for (const [k, v] of params.entries()) if (k.startsWith('cf_') && v) p.set(k, v);
    if (!p.get('companyId') && activeCompany) p.set('companyId', activeCompany);
    return p;
  }, [params, activeCompany]);
  const { data, isLoading, error } = usePipeline(queryParams);
  // Con «venta requiere separación o proforma previa» activo, «Venta» solo se ofrece desde Proforma / Separación
  // (Lote 5 · H2, P12): la regla viene en los catálogos de la empresa del tablero; sin dato, la más estricta.
  const { data: catalogs } = useCatalogs(queryParams.get('companyId') ?? undefined);
  const moveOptions = {
    requirePriorSeparationForSale: catalogs?.settings.requirePriorSeparationForSale ?? true,
  };
  const { user } = useAuth();
  const [active, setActive] = useState<PipelineCard | null>(null);
  const [move, setMove] = useState<{ lead: PipelineCard; stage: PipelineStage } | null>(null);
  // `desist` = «Devolver y desistir»: la devolución del proceso vivo con «Desistir» como siguiente paso.
  const [undo, setUndo] = useState<{ lead: PipelineCard; desist: boolean } | null>(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  // Solo quien gestiona la empresa anula procesos (P16); la web no ofrece lo que la API va a rechazar.
  const managesCompany = (companyId: string) =>
    !!user?.memberships.some((m) => m.companyId === companyId && MANAGER_ROLES.includes(m.role));
  // Devolver un proceso lo hace el asesor dueño del lead o quien gestiona (P16): desde Separación / Venta,
  // «Retroceder…» y «Devolver y desistir…» solo se ofrecen a quien puede (un asesor de sala que no es el dueño, no).
  const canUndoDeal = (lead: PipelineCard) =>
    managesCompany(lead.companyId) || (!!user && lead.advisorId === user.id);
  const notAllowedToUndoDeal = () =>
    toast.error(
      'Movimiento no permitido',
      'Solo el asesor del lead o quien gestiona la empresa puede devolver o anular la separación o venta.',
    );

  const setFilter = (key: string, value: string) =>
    setParams(applyLeadFilter(params, key, value), { replace: true });
  const applyView = (v: SavedViewDto) =>
    setParams(new URLSearchParams(v.filters), { replace: true });
  const currentFilters = Object.fromEntries(
    [...FILTER_KEYS, ...[...params.keys()].filter((k) => k.startsWith('cf_'))]
      .map((k) => [k, params.get(k) ?? ''])
      .filter(([, v]) => v),
  );
  const activeFilters = Object.keys(currentFilters).length;

  const requestMove = (lead: PipelineCard, stage: PipelineStage) => {
    if (stage === lead.pipelineStage) return;
    // Con separación o venta viva no se suelta en Perdido (la API lo rechaza: DESIST_WITH_ACTIVE_DEAL): se
    // devuelve el proceso y se desiste en el mismo paso (Lote 5 · H1).
    if (stage === 'PERDIDO' && stageHasLiveDeal(lead.pipelineStage)) {
      if (!canUndoDeal(lead)) notAllowedToUndoDeal();
      else setUndo({ lead, desist: true });
      return;
    }
    if (allowedStageMoves(lead.pipelineStage, moveOptions).includes(stage)) {
      setMove({ lead, stage });
      return;
    }
    if (stage === 'VENTA' && allowedStageMoves(lead.pipelineStage).includes(stage)) {
      toast.error(
        'Venta sin separación ni proforma',
        'Para registrar una venta el lead debe tener una separación o una proforma previa: muévelo primero a Proforma o Separación.',
      );
      return;
    }
    if (allowedStageBackMoves(lead.pipelineStage).includes(stage)) {
      if (stageUndoKind(lead.pipelineStage) === 'REFUND_DEAL' && !canUndoDeal(lead))
        notAllowedToUndoDeal();
      else setUndo({ lead, desist: false });
      return;
    }
    toast.error(
      'Movimiento no permitido',
      stageUndoKind(lead.pipelineStage)
        ? 'Para retroceder se anula el hecho de la etapa actual (cita, proforma o proceso); la etapa resultante se calcula sola.'
        : 'Los contactos y visitas son hechos ocurridos: desde esta etapa solo se avanza. Una visita registrada por error se anula desde la ficha («Anular visita», dentro de las 24 horas).',
    );
  };

  const onDragStart = (e: DragStartEvent) =>
    setActive((e.active.data.current as { lead: PipelineCard }).lead);
  const onDragEnd = (e: DragEndEvent) => {
    setActive(null);
    const lead = (e.active.data.current as { lead: PipelineCard })?.lead;
    const stage = e.over?.id as PipelineStage | undefined;
    if (lead && stage) requestMove(lead, stage);
  };

  return (
    <div className="flex h-[calc(100vh-7.5rem)] flex-col gap-3">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Leads</h1>
          <p className="text-muted-foreground text-sm" data-testid="pipeline-total">
            {data ? `${fmt.number(data.total)} leads` : 'Cargando…'} · {activeFilters} filtro
            {activeFilters === 1 ? '' : 's'}
            {activeFilters > 0 && (
              <button
                type="button"
                className="ml-1 underline"
                onClick={() => setParams(new URLSearchParams(), { replace: true })}
              >
                limpiar
              </button>
            )}
            {' · arrastra una tarjeta para avanzarla registrando el hecho'}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <ViewSwitch view="tablero" params={params} />
          <SavedViews scope="PIPELINE" currentFilters={currentFilters} onApply={applyView} />
          <Button asChild size="sm">
            <Link to="/leads/new">
              <Plus className="h-4 w-4" /> Nuevo lead
            </Link>
          </Button>
        </div>
      </div>
      <LeadFilters params={params} onChange={setFilter} />
      {error && (
        <p role="alert" className="bg-destructive/10 text-destructive rounded-md px-3 py-2 text-sm">
          {(error as Error).message}
        </p>
      )}
      <DndContext sensors={sensors} onDragStart={onDragStart} onDragEnd={onDragEnd}>
        <div className="flex min-h-0 flex-1 gap-3 overflow-x-auto pb-2" data-testid="kanban">
          {PIPELINE_STAGE_ORDER.map((stage) => {
            const col = data?.columns.find((c) => c.stage === stage);
            return (
              <Column key={stage} stage={stage} count={col?.count ?? 0} loading={isLoading}>
                {col?.items.map((lead) => (
                  <Card
                    key={lead.id}
                    lead={lead}
                    moveOptions={moveOptions}
                    canUndoDeal={canUndoDeal(lead)}
                    onOpen={() => navigate(`/leads/${lead.id}`)}
                    onMove={(s) => requestMove(lead, s)}
                    onUndo={() => setUndo({ lead, desist: false })}
                    onRefundAndDesist={() => setUndo({ lead, desist: true })}
                  />
                ))}
                {col && col.nextCursor && (
                  <Link
                    to={`/pipeline?vista=tabla&${queryParams.toString()}&pipelineStage=${stage}&sort=stage_changed_desc`}
                    className="text-muted-foreground hover:bg-accent block rounded-md border border-dashed p-2 text-center text-xs"
                  >
                    Ver los {col.count} en tabla
                  </Link>
                )}
              </Column>
            );
          })}
        </div>
        <DragOverlay>{active ? <CardBody lead={active} dragging /> : null}</DragOverlay>
      </DndContext>
      {move && move.stage === 'PROFORMA' && (
        // Mover a Proforma abre el mismo formulario completo de la ficha (lista, descuento, vigencia, notas,
        // visita): un solo formulario de proforma (Lote 5 · H2, P11).
        <QuoteDialog
          leadId={move.lead.id}
          companyId={move.lead.companyId}
          projectId={move.lead.projectId}
          open
          onOpenChange={(o) => !o && setMove(null)}
        />
      )}
      {move && move.stage !== 'PROFORMA' && (
        <StageMoveDialog
          leadId={move.lead.id}
          companyId={move.lead.companyId}
          projectId={move.lead.projectId}
          stage={move.stage}
          onOpenChange={(o) => !o && setMove(null)}
        />
      )}
      {undo && (
        <StageUndoDialog
          leadId={undo.lead.id}
          companyId={undo.lead.companyId}
          currentStage={undo.lead.pipelineStage}
          open
          desist={undo.desist}
          canAnnul={managesCompany(undo.lead.companyId)}
          onOpenChange={(o) => !o && setUndo(null)}
        />
      )}
    </div>
  );
}

function Column({
  stage,
  count,
  loading,
  children,
}: {
  stage: PipelineStage;
  count: number;
  loading: boolean;
  children: React.ReactNode;
}) {
  const { isOver, setNodeRef } = useDroppable({ id: stage });
  const tone =
    stage === 'PERDIDO'
      ? 'border-red-200'
      : stage === 'VENTA'
        ? 'border-emerald-300'
        : 'border-border';
  return (
    <section
      ref={setNodeRef}
      className={`bg-muted/40 flex w-72 shrink-0 flex-col rounded-lg border ${tone} ${isOver ? 'ring-primary ring-2' : ''}`}
      data-testid={`column-${stage}`}
    >
      <header className="flex items-center justify-between px-3 py-2">
        <h2 className="text-sm font-semibold">{STAGE_LABEL[stage]}</h2>
        <Badge tone={stage === 'PERDIDO' ? 'danger' : stage === 'VENTA' ? 'success' : 'default'}>
          {count}
        </Badge>
      </header>
      <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto px-2 pb-2">
        {loading && [0, 1, 2].map((i) => <Skeleton key={i} className="h-20 w-full" />)}
        {children}
        {!loading && count === 0 && (
          <p className="text-muted-foreground rounded-md border border-dashed p-3 text-center text-xs">
            Sin leads
          </p>
        )}
      </div>
    </section>
  );
}

function Card({
  lead,
  moveOptions,
  canUndoDeal,
  onOpen,
  onMove,
  onUndo,
  onRefundAndDesist,
}: {
  lead: PipelineCard;
  /** P12: con la regla activa, «Venta» solo desde Proforma / Separación. */
  moveOptions: StageMoveOptions;
  /** P16: quien mira puede devolver el proceso (asesor dueño o quien gestiona); si no, sin «Retroceder…» aquí. */
  canUndoDeal: boolean;
  onOpen: () => void;
  onMove: (s: PipelineStage) => void;
  onUndo: () => void;
  /** «Devolver y desistir»: con separación o venta viva reemplaza a «Perdido» (Lote 5 · H1). */
  onRefundAndDesist: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: lead.id,
    data: { lead },
  });
  const liveDeal = stageHasLiveDeal(lead.pipelineStage);
  const targets = allowedStageMoves(lead.pipelineStage, moveOptions).filter(
    (s) => s !== 'PERDIDO' || !liveDeal,
  );
  const undoKind = stageUndoKind(lead.pipelineStage);
  const canUndo = !!undoKind && (undoKind !== 'REFUND_DEAL' || canUndoDeal);
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Translate.toString(transform), opacity: isDragging ? 0.4 : 1 }}
      className="relative"
      data-testid="kanban-card"
    >
      <CardBody
        lead={lead}
        onOpen={onOpen}
        handle={{ ...attributes, ...listeners }}
        menu={
          targets.length || canUndo ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="text-muted-foreground hover:bg-accent rounded p-0.5"
                  aria-label="Mover a etapa"
                >
                  <MoreHorizontal className="h-4 w-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-44">
                {targets.length > 0 && <DropdownMenuLabel>Mover a…</DropdownMenuLabel>}
                {targets.map((s) => (
                  <DropdownMenuItem key={s} onSelect={() => onMove(s)}>
                    {STAGE_LABEL[s]}
                  </DropdownMenuItem>
                ))}
                {canUndo && (
                  <>
                    {targets.length > 0 && <DropdownMenuSeparator />}
                    <DropdownMenuItem onSelect={onUndo} data-testid="menu-undo">
                      <Undo2 className="h-4 w-4" /> Retroceder…
                    </DropdownMenuItem>
                    {liveDeal && (
                      <DropdownMenuItem
                        onSelect={onRefundAndDesist}
                        data-testid="menu-refund-desist"
                      >
                        <XCircle className="h-4 w-4" /> Devolver y desistir…
                      </DropdownMenuItem>
                    )}
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null
        }
      />
    </div>
  );
}

function CardBody({
  lead,
  onOpen,
  handle,
  menu,
  dragging,
}: {
  lead: PipelineCard;
  onOpen?: () => void;
  handle?: Record<string, unknown>;
  menu?: React.ReactNode;
  dragging?: boolean;
}) {
  return (
    <article
      className={`bg-card rounded-md border p-2.5 text-sm shadow-sm ${dragging ? 'rotate-1 shadow-lg' : ''}`}
    >
      <div className="flex items-start gap-1">
        {handle && (
          <button
            type="button"
            className="text-muted-foreground hover:text-foreground mt-0.5 cursor-grab touch-none"
            aria-label="Arrastrar"
            {...handle}
          >
            <GripVertical className="h-4 w-4" />
          </button>
        )}
        <button
          type="button"
          onClick={onOpen}
          className="min-w-0 flex-1 text-left font-medium hover:underline"
        >
          {lead.contactName}
        </button>
        {menu}
      </div>
      <p className="text-muted-foreground mt-0.5 truncate text-xs">
        {lead.projectName} · {lead.advisorName ?? 'Sin asesor'}
      </p>
      {lead.tags.length > 0 && (
        <div className="mt-1">
          <TagChips tags={lead.tags} max={2} />
        </div>
      )}
      <div className="mt-2 flex flex-wrap items-center gap-1">
        <LeadFollowupBadge lead={lead} />
        <ScoreBadge score={lead.score} />
        <span className="text-muted-foreground ml-auto text-[11px]" title="Días en esta etapa">
          {lead.daysInStage} d
        </span>
      </div>
    </article>
  );
}
